import React, { useState, useMemo, memo, useCallback } from "react";
import cx from "classnames";
import { css } from "@linaria/core";
import { colors, shadows, spacings } from "@ui/Theme";
import Text from "@ui/Text";
import t from "../../../../../../../../../i18n";
import { useAnnotationBehavior } from "./hook/useAnnotationBehavior";
import formatNumber from "@ui/i18n/formatNumber";
import Ellipsis from "@ui/Ellipsis";
import { overlayBoundary } from "@ui/ImageViewer";
import TooltipV2 from "@ui/TooltipV2";
import ButtonIconV2 from "@ui/ButtonIconV2";
import {
  ArrowRightDownLine,
  ArrowRightUpLine,
  CloseLine,
  Database2Fill,
  InformationLine,
  Pointer,
  ShareBoxLine,
  CircleFill,
  CircleLine,
  TriangleFill,
} from "@ui/IconV2/react";
import { CloseCircleFill, InformationFill } from "@ui/IconV2/react";
import { Thresholds } from "diagnostics-domain/dashboard/type";
import { translationLocale } from "@ui/i18n";
import { useTranslations } from "../../../../../../../../../api/useTranslations";
import { needAbsoluteValueEvolution } from "../Annotation/utils";
import { getRoleIcon, getRoleColor, resolveRole } from "../roleConfig";
import { useThreshold } from "../useThreshold";
import type { Annotation } from "./hook/useAnnotations";
import { DiagramAnnotationFaultStatus } from "./FaultStatus";
import {
  AGGREGATE_UNEXPLAINED_LOSSES,
  useAvailableResources,
} from "../../../../../../hook/useAvailableResources";
import PriorityFill from "@ui/IconV2/react/PriorityFill";
import type { PriorityFaultEvent } from "diagnostics-domain/faultEvent";
import classNames from "classnames";
import { toBasenamePath } from "../../../../../../../../../router/helpers/navigation";
import { toPath } from "../../../../../../../../../router/helpers/path";
import { EventName } from "../../../../../../../Events/components/EventName";

const EVENT_VALUE = "event_impact";
const EVENT_VALUE_INTERACTIVE = "event_impact_interactive";
const VALUE_ONLY = "value_only";
const HIDE_BOTTOM_BORDER = "hide_bottom_border";

type DiagramAnnotationInfoProps = {
  children: React.ReactNode | ((hovered: boolean) => React.ReactNode);
  hovered?: boolean;
};

export const DiagramAnnotation = memo(
  ({
    projectId,
    anchor,
    children,
    index,
    position,
    anchorRelativePositions,
    selected,
    expanded,
    visible,
    annotation,
    isRoleDisplayed,
    onPositionChange,
    onRelativePositionChange,
    onPointerDown,
    onExpansionChange,
    isDashboardEditing,
  }: React.PropsWithChildren<{
    projectId: string;
    anchor: { x: "left" | "center" | "right"; y: "top" | "center" | "bottom" };
    index: number;
    position: [number, number, number, number];
    anchorRelativePositions: [number, number];
    selected: boolean;
    expanded: boolean;
    visible: boolean;
    annotation: Annotation;
    isRoleDisplayed: boolean;
    isDashboardEditing: boolean;
    onPositionChange?: (position: [number, number, number, number], index: number) => void;
    onRelativePositionChange?: (position: [number, number], index: number) => void;
    onPointerDown?: (index: number) => void;
    onExpansionChange: () => void;
  }>): React.JSX.Element => {
    const { annotationRef, annotationDataRef, annotationResizerRef, area, labelOnly } =
      useAnnotationBehavior({
        index,
        position,
        anchorRelativePositions,
        anchor,
        updateAnnotationAnchorPosition: onPositionChange,
        updateAnnotationRelativePosition: onRelativePositionChange,
        selectAnnotation: onPointerDown,
      });

    const [hovered, setHovered] = useState(false);
    const availableResources = useAvailableResources({ projectId });

    const showLabel = annotation.showLabel || (!isDashboardEditing && hovered);

    const thresholdValue = useMemo((): number | null => {
      if (!annotation.thresholds?.length) return null;

      const entry = annotation.values.find(
        (v): v is typeof v & { value: number } =>
          typeof v.value === "number" && annotation.thresholds.some(th => th.column === v.column),
      );

      return entry?.value ?? null;
    }, [annotation.values, annotation.thresholds]);

    const { thresholdColor, thresholdDescription } = useThreshold({
      thresholds: annotation.thresholds,
      annotationValues: annotation.values,
      thresholdValue,
    });

    const resolvedRole = useMemo(
      () => resolveRole(annotation, availableResources, isRoleDisplayed),
      [annotation, availableResources, isRoleDisplayed],
    );

    const RoleIcon = useMemo(() => {
      const fallbackRoles = new Set([
        "MODEL_INPUT",
        "OBSERVABLE",
        "INACTIVE_OBSERVABLE",
        "PERFORMANCE_INDICATOR",
        "QUANTITY",
      ]);

      if (!isRoleDisplayed && fallbackRoles.has(resolvedRole)) {
        return Database2Fill;
      }

      return getRoleIcon(resolvedRole);
    }, [isRoleDisplayed, resolvedRole]);

    const arrowColor = useMemo(() => {
      if (!isRoleDisplayed && resolvedRole === "INACTIVE_OBSERVABLE") {
        return colors.chart_categorical_blue;
      }
      return getRoleColor(resolvedRole);
    }, [isRoleDisplayed, resolvedRole]);

    const translations = useTranslations();

    const roleLabel =
      isRoleDisplayed ||
      !["MODEL_INPUT", "OBSERVABLE", "INACTIVE_OBSERVABLE", "PERFORMANCE_INDICATOR"].includes(
        resolvedRole,
      )
        ? t("project.dashboard.role.label", resolvedRole, translations)
        : t("project.dashboard.role.label", "QUANTITY", translations);

    const [tooltipOpened, setTooltipOpened] = useState<boolean>(false);
    const toggleMinification = useCallback(() => {
      onExpansionChange();
      setTooltipOpened(false);
    }, [onExpansionChange]);

    return (
      <div
        ref={annotationRef}
        className={cx(diagramAnnotation, {
          area,
          selected,
          editable: Boolean(onPositionChange),
          expanded,
          visible,
          overlayBoundary,
          labelOnly,
        })}
      >
        <div
          ref={annotationDataRef}
          onMouseEnter={() => {
            if (!isDashboardEditing) setHovered(true);
          }}
          onMouseLeave={() => {
            if (!isDashboardEditing) setHovered(false);
          }}
          onClick={() => !expanded && toggleMinification()}
          className={cx(diagramAnnotationData, expanded && overlayBoundary, {
            area,
            expanded,
            editable: Boolean(onPositionChange),
            visible,
            selected,
            labelOnly,
            inactive: isRoleDisplayed && resolvedRole === "INACTIVE_OBSERVABLE",
            system: resolvedRole === "SYSTEM",
            hasThreshold: Boolean(thresholdColor),
            fault: resolvedRole === "FAULT",
            unexplainedLosses: resolvedRole === "UNEXPLAINED_LOSSES",
            forceShowLabel: showLabel,
          })}
        >
          {!expanded && thresholdColor && (
            <TooltipV2 overlay={thresholdDescription}>
              <div
                className={diagramAnnotationThreshold}
                style={{ backgroundColor: thresholdColor }}
              >
                <InformationLine color={colors.carbon800} />
              </div>
            </TooltipV2>
          )}
          <div className={diagramAnnotationLabel}>
            <div className={diagramAnnotationLabelContent}>
              {expanded && showLabel && (
                <TooltipV2 overlay={roleLabel}>
                  <span
                    className={cx(
                      resolvedRole === "INACTIVE_OBSERVABLE" && !isRoleDisplayed
                        ? fillIconInactiveFillColor
                        : resolvedRole === "INACTIVE_OBSERVABLE"
                          ? fillIconInactiveObservable
                          : fillIconWithContextColor,
                    )}
                    style={{
                      color:
                        resolvedRole === "INACTIVE_OBSERVABLE" && !isRoleDisplayed
                          ? colors.carbon800
                          : getRoleColor(resolvedRole),
                    }}
                  >
                    <RoleIcon size="xs" />
                  </span>
                </TooltipV2>
              )}
              {expanded &&
                showLabel &&
                annotation.type !== "AGGREGATE" &&
                annotation.encodings.every(e => e.identifier !== AGGREGATE_UNEXPLAINED_LOSSES) && (
                  <DiagramAnnotationFaultStatus annotationValues={annotation.values} />
                )}
              {showLabel && (
                <span className={diagramAnnotationLabelTitle}>
                  <Text type="body3SemiBold">{annotation.name}</Text>
                </span>
              )}
            </div>
            <TooltipV2
              openDelay={0}
              overlay={
                expanded
                  ? t("project.dashboard.block.diagram.full_annotation.close")
                  : t("project.dashboard.block.diagram.full_annotation.open")
              }
              closeOnOutsideClick
              closeDelay={0}
              opened={tooltipOpened}
              setOpened={setTooltipOpened}
            >
              <ButtonIconV2
                onClick={() => expanded && toggleMinification()}
                onPointerDownCapture={e => e.stopPropagation()}
                onPointerEnter={() => setTooltipOpened(true)}
                onPointerLeave={() => setTooltipOpened(false)}
                theme={expanded ? "light" : "dark"}
                className={cx(diagramAnnotationMinToggle, { expanded }, !expanded && "minified")}
              >
                {expanded ? <CloseLine size="xs" /> : <RoleIcon size="m" color={colors.hydrogen} />}
              </ButtonIconV2>
            </TooltipV2>
          </div>
          {React.Children.toArray(children).map(child => {
            if (isDiagramAnnotationInfoElement(child)) {
              if (!showLabel) return null;
              return React.cloneElement(child, { hovered });
            }
            return child;
          })}

          {!expanded && !labelOnly && (
            <div
              className={diagramAnnotationArrow}
              onClick={e => {
                toggleMinification();
                e.stopPropagation();
              }}
            >
              <Pointer width={16} height={10} color={arrowColor} />
            </div>
          )}
        </div>
        {area && expanded && Boolean(onPositionChange) && (
          <div ref={annotationResizerRef} className={diagramAnnotationResizer} />
        )}
      </div>
    );
  },
);

function isDiagramAnnotationInfoElement(
  element: React.ReactNode,
): element is React.ReactElement<DiagramAnnotationInfoProps, typeof DiagramAnnotationInfo> {
  return React.isValidElement(element) && element.type === DiagramAnnotationInfo;
}

export const DiagramAnnotationInfo = memo(
  ({ children, hovered = false }: DiagramAnnotationInfoProps) => {
    if (!children) return null;

    const content = typeof children === "function" ? children(hovered) : children;

    return (
      <Text type="body3" className={diagramAnnotationInfo}>
        <Ellipsis maxNumberOfLines={2}>{content}</Ellipsis>
      </Text>
    );
  },
);

export const DiagramAnnotationEmpty = memo(({ children }: React.ComponentProps<typeof Text>) => (
  <Text type="body3" className={diagramAnnotationEmpty}>
    {children}
  </Text>
));

export const DiagramAnnotationValue = memo(
  ({
    projectId,
    aggregated,
    annotationValue,
    thresholdValue,
    thresholds,
    annotationType,
    singleGapValue,
    hasSingleAnnotations = false,
    hideBottomBorder = false,
    showPartLoadSensitiveKpis = true,
  }: {
    projectId: string;
    aggregated: boolean;
    annotationValue: Annotation["values"][number];
    thresholdValue: number | null;
    thresholds: Thresholds;
    annotationType: string | null;
    singleGapValue: boolean;
    hasSingleAnnotations: boolean;
    hideBottomBorder?: boolean;
    showPartLoadSensitiveKpis?: boolean;
  }) => {
    const unit = annotationValue.unit === "null" ? null : annotationValue.unit;
    const translations = useTranslations();

    const invalid = annotationValue.column === "measured" && annotationValue.hasInvalidities;

    const { matchingThreshold, thresholdColor, thresholdDescription } = useThreshold({
      thresholds,
      thresholdValue,
      annotationValues: [
        { column: annotationValue.column, unit: typeof unit === "string" ? unit : null },
      ],
    });

    const backgroundColor = thresholdColor || "transparent";

    const noValueReason = useMemo(() => {
      if (annotationValue.column === "unexplainedLosses")
        return t(
          "project.dashboard.block.diagram.undefined_unexplained_losses",
          t("charts.legend.unexplained_losses", translations),
        );
      if (annotationValue.column === "gap")
        return t("project.dashboard.block.diagram.undefined_gap");
      if (annotationValue.column === "normalizedGap")
        return t("project.dashboard.block.diagram.undefined_normalized_gap");
      if (annotationValue.column === "std")
        return t("project.dashboard.block.diagram.std_unavailable");
      if (annotationValue.column === "expected")
        return t("project.dashboard.block.diagram.expected_unavailable");
      if (
        annotationValue.column === "externalImpacts" &&
        typeof annotationValue.quantity === "string"
      )
        return t(
          "project.dashboard.block.diagram.external_impacts_unavailable",
          annotationValue.quantity,
        );
      return null;
    }, [annotationValue.column, annotationValue.quantity, translations]);

    const label = useMemo(() => {
      const base = `${annotationValue.quantity ? " " + annotationValue.quantity : ""}${translationLocale === "fr" ? " : " : ": "}`;

      if (
        (annotationValue.column === "systemImpact" ||
          annotationValue.column === "unexplainedLosses" ||
          annotationValue.column === "noSystemImpacts" ||
          annotationValue.column === "quantityImpacts") &&
        typeof annotationValue.quantity === "string"
      ) {
        return t(
          "project.dashboard.resource.column.annotation.label.systemImpact",
          translations,
          annotationValue.quantity,
          annotationValue.hasMultiplePIs ?? false,
        );
      }

      if (
        annotationValue.column === "eventImpact" &&
        annotationValue.name &&
        annotationValue.name.length > 0
      ) {
        return (
          <EventName
            name={annotationValue.name}
            hasMultiFault={annotationValue.event?.hasMultiFault === true}
          />
        );
      }

      if (annotationValue.column === "unexplainedLosses") return base;

      // Pour les colonnes de grandeurs, utiliser des icônes compactes au lieu de texte
      const column = annotationValue.column ?? "";
      const fullLabel = t("project.dashboard.resource.column.annotation", column);

      if (column === "measured") {
        return (
          <TooltipV2 overlay={fullLabel + base.trim()}>
            <span className={diagramAnnotationLabelIcon}>
              <CircleFill size="xs" color={colors.titanium600} />
              {annotationValue.quantity && <span>{base}</span>}
            </span>
          </TooltipV2>
        );
      }

      if (column === "expected") {
        return (
          <TooltipV2 overlay={fullLabel + base.trim()}>
            <span className={diagramAnnotationLabelIcon}>
              <CircleLine size="xs" color={colors.titanium600} />
              {annotationValue.quantity && <span>{base}</span>}
            </span>
          </TooltipV2>
        );
      }

      if (column === "std") {
        return (
          <TooltipV2 overlay={fullLabel + base.trim()}>
            <span className={diagramAnnotationLabelIcon}>
              <span className={diagramAnnotationStdLabel}>STD</span>
              {annotationValue.quantity && <span>{base}</span>}
            </span>
          </TooltipV2>
        );
      }

      if (column === "gap") {
        return (
          <TooltipV2 overlay={fullLabel + base.trim()}>
            <span className={diagramAnnotationLabelIcon}>
              <TriangleFill size="xs" color={colors.titanium600} />
              {annotationValue.quantity && <span>{base}</span>}
            </span>
          </TooltipV2>
        );
      }

      if (column === "normalizedGap" && singleGapValue) {
        return (
          <TooltipV2 overlay={fullLabel + base.trim()}>
            <span className={diagramAnnotationLabelIcon}>
              <TriangleFill size="xs" color={colors.titanium600} />
              {annotationValue.quantity && <span>{base}</span>}
            </span>
          </TooltipV2>
        );
      } else {
        return <span className={diagramAnnotationLabelSpacer} />;
      }

      return `${fullLabel}${base}`;
    }, [
      annotationValue.column,
      annotationValue.quantity,
      annotationValue.name,
      annotationValue.event?.hasMultiFault,
      annotationValue.hasMultiplePIs,
      translations,
      singleGapValue,
    ]);

    const isUsingAbsoluteCalculation = useMemo(
      () =>
        annotationType && annotationValue.column
          ? needAbsoluteValueEvolution(annotationType, annotationValue.column)
          : false,
      [annotationType, annotationValue.column],
    );

    const isGapColumn =
      annotationValue.column === "gap" || annotationValue.column === "normalizedGap";
    const formattedMin =
      typeof annotationValue.min === "number"
        ? formatNumber(annotationValue.min, {
            precision: aggregated ? 1 : 2,
            sign: isGapColumn,
          })
        : null;
    const formattedMax =
      typeof annotationValue.max === "number"
        ? formatNumber(annotationValue.max, {
            precision: aggregated ? 1 : 2,
            sign: isGapColumn,
          })
        : null;
    const formattedValue =
      typeof annotationValue.value === "number"
        ? formatNumber(annotationValue.value, {
            precision: aggregated ? 1 : 2,
            sign: isGapColumn,
          })
        : null;
    const formattedValueWithUnit =
      formattedValue === null ? null : `${formattedValue} ${annotationValue.unit}`;
    const isValueOnly = hasSingleAnnotations && annotationValue.column === "measured";
    const hidePartLoadSensitiveKpis =
      !showPartLoadSensitiveKpis && annotationValue.column === "eventImpact";

    const redirectEvent = useCallback(() => {
      if (
        annotationValue.column !== "eventImpact" ||
        annotationValue.priority === "LOW" ||
        !annotationValue.event
      ) {
        return;
      }

      const path =
        annotationValue.event.type === "DIAGNOSED"
          ? toPath("/projects/:projectId/fault-events/:faultEventId/fault", {
              projectId,
              faultEventId: annotationValue.event.id,
            })
          : toPath("/projects/:projectId/fault-events/manual/:manualFaultEventId/fault", {
              projectId,
              manualFaultEventId: annotationValue.event.id,
            });
      window.open(toBasenamePath(path));
    }, [projectId, annotationValue.column, annotationValue.priority, annotationValue.event]);

    return (
      <Text
        type="body3"
        className={classNames(diagramAnnotationValue, {
          [EVENT_VALUE]: annotationValue.column === "eventImpact",
          [EVENT_VALUE_INTERACTIVE]: annotationValue.priority !== "LOW",
          [VALUE_ONLY]: isValueOnly,
          [HIDE_BOTTOM_BORDER]: hideBottomBorder,
          alignLeft: ["measured", "std", "expected", "gap", "normalizedGap"].includes(
            annotationValue.column,
          ),
        })}
        onClick={redirectEvent}
        style={{ backgroundColor }}
      >
        <div className={diagramEventAnnotationValue}>
          {typeof annotationValue.priority === "string" && annotationValue.priority.length > 0 && (
            <PriorityFill priority={annotationValue.priority as PriorityFaultEvent} />
          )}
          {/* Pour "measured" avec une seule annotation, on affiche toujours l'icône cercle */}
          {hasSingleAnnotations && annotationValue.column === "measured" ? (
            <TooltipV2 overlay={t("project.dashboard.resource.column.annotation", "measured")}>
              <span className={diagramAnnotationLabelIconOnly}>
                <CircleFill size="xs" color={colors.titanium600} />
              </span>
            </TooltipV2>
          ) : hasSingleAnnotations && annotationValue.column === "std" ? (
            <TooltipV2 overlay={t("project.dashboard.resource.column.annotation", "std")}>
              <span className={diagramAnnotationLabelIconOnly}>
                <span className={diagramAnnotationStdLabel}>STD</span>
              </span>
            </TooltipV2>
          ) : (
            label
          )}
          {annotationValue.column === "eventImpact" && annotationValue.priority !== "LOW" && (
            <ShareBoxLine
              className="eventLinkIcon"
              color={colors.lightNitrogen500_Main}
              size="xs"
            />
          )}
        </div>
        <Text
          as="span"
          type={
            annotationValue.column === "expected" || annotationValue.column === "normalizedGap"
              ? "body3"
              : "body3SemiBold"
          }
          className={diagramAnnotationValueData}
        >
          {annotationValue.evolution === -1 && (
            <TooltipV2
              overlay={
                isUsingAbsoluteCalculation
                  ? t("project.dashboard.block.diagram.evolution.down.absolute")
                  : t("project.dashboard.block.diagram.evolution.down")
              }
            >
              <span>
                <ArrowRightDownLine />
              </span>
            </TooltipV2>
          )}
          {annotationValue.evolution === 1 && (
            <TooltipV2
              overlay={
                isUsingAbsoluteCalculation
                  ? t("project.dashboard.block.diagram.evolution.up.absolute")
                  : t("project.dashboard.block.diagram.evolution.up")
              }
            >
              <span>
                <ArrowRightUpLine />
              </span>
            </TooltipV2>
          )}
          {invalid && formattedValue && (
            <TooltipV2
              overlay={
                annotationValue.min && annotationValue.max
                  ? t("flags_legend.quantity_flag.invalid_value.median")
                  : t("flags_legend.quantity_flag.invalid_value")
              }
            >
              <span>
                <CloseCircleFill color={thresholdColor ? colors.carbon800 : colors.oxygen200} />
              </span>
            </TooltipV2>
          )}
          {hidePartLoadSensitiveKpis
            ? null
            : (formattedValueWithUnit ??
              (annotationValue.column === "systemImpact" ? (
                `0 ${unit}`
              ) : noValueReason ? (
                <TooltipV2 overlay={noValueReason}>
                  <span>
                    <CloseCircleFill color={colors.carbon400} />
                  </span>
                </TooltipV2>
              ) : (
                <CloseCircleFill color={colors.carbon400} />
              )))}
          {formattedValue && formattedMin && formattedMax ? (
            <Text as="span" type="body3" className={diagramAnnotationValueMinMax}>
              {formattedMin === formattedMax && formattedMin === formattedValue
                ? ""
                : `[${formattedMin} ; ${formattedMax}]`}
            </Text>
          ) : null}
          {matchingThreshold && matchingThreshold.value !== null && (
            <TooltipV2 overlay={thresholdDescription}>
              <span>
                <InformationFill color={colors.carbon800} />
              </span>
            </TooltipV2>
          )}
        </Text>
      </Text>
    );
  },
);

//#region CSS
const diagramAnnotation = css`
  color: ${colors.titanium900};
  background-color: ${colors.titanium900};
  border-radius: 50%;
  height: 12px;
  left: calc(100% * var(--annotation-x, 0));
  position: absolute;
  top: calc(100% * var(--annotation-y, 0));
  transform: translate3d(-50%, -50%, 0);
  width: 12px;
  will-change: top, left;
  resize: both;
  user-select: none;

  &.editable {
    background-color: currentColor;
  }

  > * {
    white-space: nowrap;
  }

  &[style*="--annotation-w"][style*="--annotation-h"] {
    background-color: transparent;
    border: 1px solid currentColor;
    border-radius: 4px;
    height: calc(100% * var(--annotation-h, 0));
    width: calc(100% * var(--annotation-w, 0));
    will-change: top, left, width, height;
    box-shadow: ${shadows.shadow200};
  }

  &.area:not(.expanded)[style*="--annotation-w"][style*="--annotation-h"] {
    width: 0;
    height: 0;
    border: none;
    box-shadow: none;
  }

  &:not(.labelOnly)::before {
    background-color: currentColor;
    display: block;
    content: "";
    height: 1px;
    width: calc(1px * var(--annotation-stroke-length));
    position: absolute;
    top: 50%;
    left: 50%;
    transform-origin: left center;
    transform: translate(
        calc(1px * var(--annotation-stroke-x)),
        calc(1px * var(--annotation-stroke-y))
      )
      rotate(calc(1deg * var(--annotation-stroke-rotation, 0deg)));
  }

  &:not(.expanded) {
    background-color: transparent;
    border: none;
    box-shadow: none;
  }

  &:not(.expanded)::before {
    display: none;
  }

  &:hover {
    cursor: pointer;
  }

  &.editable:hover {
    cursor: grab;
  }

  &.selected {
    z-index: 1;
  }

  &.editable.selected {
    color: ${colors.lightNitrogen500_Main};
  }

  &:not(.visible) {
    display: none;
  }
`;

const diagramAnnotationResizer = css`
  width: 12px;
  height: 12px;
  position: absolute;
  bottom: 0;
  right: 0;
  cursor: se-resize;
  background-image: linear-gradient(
    -45deg,
    transparent 0%,
    currentColor 10%,
    transparent 20%,
    currentColor 30%,
    transparent 40%,
    currentColor 50%,
    transparent 60%
  );
`;

const diagramAnnotationEmpty = css`
  color: ${colors.carbon600};
  text-align: center;
  padding: ${spacings.xxs} ${spacings.xs};

  :not(.expanded) & {
    display: none;
  }
`;

const diagramAnnotationData = css`
  display: grid;
  grid-auto-columns: min-content;
  position: absolute;
  background-color: ${colors.hydrogen};
  border: 1px solid ${colors.titanium700};
  color: ${colors.carbon900};
  border-radius: 4px;
  height: fit-content;
  box-shadow: ${shadows.shadow200};
  transition: box-shadow 0.15s ease-out;
  left: 50%;
  top: 50%;
  overflow: visible;
  transform: translate3d(
    calc(-50% + var(--annotation-data-x, 0) * var(--image-viewer-scale, 1) * 1px),
    calc(-50% + var(--annotation-data-y, 0) * var(--image-viewer-scale, 1) * 1px),
    0
  );
  z-index: 1;

  &.hasThreshold {
    border-radius: 0 0 4px 4px;
  }

  &.labelOnly {
    transform: translate3d(-50%, -50%, 0);
  }

  &.editable.selected {
    border-width: 2px;
    border-color: ${colors.lightNitrogen500_Main};
  }

  &:not(.visible) {
    display: none;
  }

  &:has(.${diagramAnnotationEmpty}) {
    border-color: ${colors.titanium500};
  }

  &.dragging {
    box-shadow: ${shadows.shadow300};
  }

  &:not(.expanded) {
    background-color: ${colors.chart_categorical_blue};
    width: 24px;
    height: 24px;
    display: flex;
    align-items: center;
    justify-content: center;
    border: 0;
    min-width: unset;
  }

  &:not(.expanded):not(.labelOnly) {
    transform: translate3d(-50%, -122%, 0);
  }

  &:not(.expanded).labelOnly {
    transform: translate3d(-50%, -50%, 0);
  }

  &.inactive:not(.expanded) {
    background-color: ${colors.titanium500};
  }

  &.fault:not(.expanded) {
    background-color: #9467bd;
  }

  &.system:not(.expanded) {
    background-color: #8c564b;
  }

  &.unexplainedLosses:not(.expanded) {
    background-color: ${colors.titanium800};
  }
`;

const diagramAnnotationInfo = css`
  color: ${colors.carbon700};
  background-color: ${colors.titanium50};
  white-space: normal;
  padding: ${spacings.xxs} ${spacings.xs};

  .${diagramAnnotationData}:has(.${diagramAnnotationEmpty}) & {
    color: ${colors.carbon700};
    svg {
      fill: ${colors.carbon700};
    }
  }

  .${diagramAnnotationData}:not(.expanded) & {
    display: none;
  }

  &:has(span:empty) {
    display: none;
  }
`;

const diagramAnnotationLabelContent = css`
  display: flex;
  align-items: center;
  gap: 2px;
  min-width: 0;
  overflow: hidden;
  flex: 1;
`;

const diagramAnnotationLabelTitle = css`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  min-width: 0;

  p {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`;

const diagramAnnotationLabel = css`
  display: none;
  position: relative;
  justify-content: space-between;
  align-items: center;
  gap: 2px;
  text-overflow: ellipsis;
  overflow: hidden;
  justify-content: flex-start;
  background-color: ${colors.titanium50};
  padding: ${spacings.xxs} ${spacings.xs};
  color: ${colors.carbon900};

  &.expanded {
    min-width: 78px;
    max-width: 200px;
    padding-right: 28px;
  }

  &:has(+ .${diagramAnnotationInfo}) {
    padding-bottom: 4px;
  }

  + .${diagramAnnotationInfo} {
    padding-top: 0;
  }

  svg {
    display: block;
  }

  .${diagramAnnotationData}:not(.expanded) & {
    padding: 0;
    background-color: ${colors.chart_categorical_blue};
    border-radius: 4px;
    transition: box-shadow 150ms ease-in-out;
    gap: unset;

    p {
      display: none;
    }
  }

  .${diagramAnnotationData}.inactive:not(.expanded) & {
    background-color: ${colors.titanium500};
  }

  .${diagramAnnotationData}.fault:not(.expanded) & {
    background-color: #9467bd;
  }

  .${diagramAnnotationData}.system:not(.expanded) & {
    background-color: #8c564b;
  }

  .${diagramAnnotationData}.unexplainedLosses:not(.expanded) & {
    background-color: ${colors.titanium800};
  }

  .${diagramAnnotationData}.forceShowLabel & {
    display: flex;
  }

  .${diagramAnnotationData}:not(.expanded) & {
    display: flex;
  }
`;

const diagramAnnotationMinToggle = css`
  margin-left: auto;
  transition: opacity 150ms ease-in-out;

  /* Minimized: icon always visible */
  .${diagramAnnotationData}:not(.expanded) & {
    opacity: 1;
  }

  /* Expanded: close button only on hover, overlay at end of title */
  .${diagramAnnotationData}.expanded & {
    position: absolute;
    right: ${spacings.xxs};
    flex-shrink: 0;
    top: 50%;
    bottom: 0;
    margin-left: 0;
    display: flex;
    align-items: center;
    padding: 4px;
    background: linear-gradient(to right, transparent, ${colors.titanium50} 10px);
    opacity: 0;
    height: max-content;
    pointer-events: none;
    transform: translateY(-50%);
  }

  .${diagramAnnotationData}.expanded:hover & {
    opacity: 1;
    pointer-events: auto;
  }

  svg {
    transform: translateZ(0);
  }

  .${diagramAnnotationData}:not(.expanded) & {
    padding: 0;
    margin: auto;
  }

  /* Minimized: 14px icon for smaller annotations */
  .${diagramAnnotationData}:not(.expanded) & svg {
    width: 14px;
    height: 14px;
  }

  .dragging & {
    pointer-events: none;
  }
`;

const diagramEventAnnotationValue = css`
  display: flex;
  align-items: center;
  gap: ${spacings.xxs};
`;

const diagramAnnotationValue = css`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 4px;
  padding: ${spacings.xxs} ${spacings.xs};
  white-space: nowrap;
  color: ${colors.carbon800};

  &.alignLeft {
    justify-content: flex-start;
  }

  .${diagramAnnotationData}:not(.expanded) & {
    display: none;
  }

  &:not(:last-child) {
    border-bottom: 1px solid ${colors.titanium200};
  }

  &.${HIDE_BOTTOM_BORDER} {
    border-bottom: none;
    padding-bottom: 2px;
  }

  &.${EVENT_VALUE} {
    border-bottom: 1px solid transparent;

    .eventLinkIcon {
      visibility: hidden;
    }

    &.${EVENT_VALUE_INTERACTIVE} {
      padding-top: ${spacings.xxs};
    }

    &.${EVENT_VALUE_INTERACTIVE}:hover {
      cursor: pointer;
      background-color: ${colors.lightNitrogen50} !important;
      color: ${colors.lightNitrogen500_Main};

      .${diagramEventAnnotationValue} {
        text-decoration: underline;
      }

      .eventLinkIcon {
        visibility: visible;
      }
    }
  }

  &.${EVENT_VALUE} + *:not(.${EVENT_VALUE}) {
    border-top: 1px solid ${colors.titanium200};
  }

  &.${VALUE_ONLY} {
    justify-content: flex-start;
    gap: 4px;
  }
`;

const diagramAnnotationValueMinMax = css`
  color: ${colors.carbon700};

  &:empty {
    display: none;
  }
`;

const diagramAnnotationValueData = css`
  display: flex;
  align-items: center;
  gap: ${spacings.xxs};

  span svg {
    display: block;
  }
`;

const diagramAnnotationThreshold = css`
  position: absolute;
  width: 24px;
  height: 18px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px 4px 0 0;
  box-shadow: ${shadows.shadow100};
  bottom: 100%;

  svg {
    width: 14px;
    height: 14px;
  }
`;

const diagramAnnotationArrow = css`
  position: absolute;
  bottom: -14px;
  cursor: pointer;

  svg path {
    fill: currentColor;
    stroke: currentColor;
  }
`;

const fillIconInactiveObservable = css`
  path:not(:last-of-type),
  rect {
    fill: ${colors.titanium500};
  }

  path:last-of-type {
    fill: ${colors.hydrogen};
  }
`;

const fillIconWithContextColor = css`
  color: currentColor;
  path {
    fill: currentColor;
  }
  rect {
    fill: currentColor;
  }
`;

const fillIconInactiveFillColor = css`
  path,
  rect {
    fill: ${colors.chart_categorical_blue};
  }
`;

const diagramAnnotationLabelIcon = css`
  display: inline-flex;
  align-items: center;
  gap: 2px;
`;

const diagramAnnotationLabelIconOnly = css`
  display: inline-flex;
  align-items: center;
`;

const diagramAnnotationStdLabel = css`
  color: ${colors.titanium600};
  font-size: 10px;
  font-weight: 700;
  line-height: 1;
`;

const diagramAnnotationLabelSpacer = css`
  display: inline-block;
  width: 12px; /* Largeur de l'icône xs pour aligner les valeurs sans icône */
`;
//#endregion CSS
